/**
 * The weekly Tip Sheet: what goes to the model, what comes back, and the two
 * decisions the app makes around it without a model at all (whose team is
 * yours, and who is starting somebody who can't play).
 *
 * Everything here is pure and shared by the API (which assembles inputs and
 * stores outputs) and the web (which renders them). The prompt text and the
 * Anthropic call live in the API; the *shapes* live here so both ends agree.
 */
import { nflGameFor, byeTeams } from './pro-schedule.js';
import { allPlay } from './derived.js';
import { depthLabel } from './depth-chart.js';
// ---------------------------------------------------------------------------
// Assembly
// ---------------------------------------------------------------------------
function recordOf(team) {
    if (!team)
        return '0-0';
    return team.ties ? `${team.wins}-${team.losses}-${team.ties}` : `${team.wins}-${team.losses}`;
}
function playerInput(p, schedule, week, depth) {
    const entry = depth?.byPlayer[p.playerId];
    return {
        playerId: p.playerId,
        name: p.name,
        slot: p.slot,
        position: p.position,
        proTeam: p.proTeam,
        started: p.started,
        ...(p.injury ? { injury: p.injury } : {}),
        projected: p.projected,
        game: nflGameFor(schedule, p.proTeamId ?? p.proTeam, week),
        ...(entry ? { depth: depthLabel(entry) } : {}),
    };
}
/**
 * One week of lineups, matchups and the NFL schedule, folded into what a
 * per-team prompt needs. Teams come back in `owners` order, so two runs over the
 * same data produce the same prompts — which is what lets the shared week
 * context cache across every team's call.
 */
export function buildTipSheetInput(lineups, matchups, schedule, owners, week, teams = [], freeAgents = [], depth = null) {
    const lineupOf = new Map(lineups.teams.map((t) => [t.ownerId, t]));
    const teamOf = new Map(teams.map((t) => [t.ownerId, t]));
    const ownerOf = new Map(owners.map((o) => [o.id, o]));
    const opponentOf = new Map();
    for (const m of matchups) {
        if (m.week !== week)
            continue;
        opponentOf.set(m.homeOwnerId, { id: m.awayOwnerId, isPlayoff: m.isPlayoff });
        opponentOf.set(m.awayOwnerId, { id: m.homeOwnerId, isPlayoff: m.isPlayoff });
    }
    const out = [];
    for (const owner of owners) {
        const lineup = lineupOf.get(owner.id);
        if (!lineup)
            continue;
        const opp = opponentOf.get(owner.id);
        const oppOwner = opp ? ownerOf.get(opp.id) : undefined;
        const oppLineup = opp ? lineupOf.get(opp.id) : undefined;
        out.push({
            ownerId: owner.id,
            ownerName: owner.name,
            teamName: owner.teamName,
            record: recordOf(teamOf.get(owner.id)),
            projected: lineup.projected,
            players: [...lineup.starters, ...lineup.bench].map((p) => playerInput(p, schedule, week, depth)),
            opponent: opp && oppOwner
                ? {
                    ownerId: oppOwner.id,
                    ownerName: oppOwner.name,
                    teamName: oppOwner.teamName,
                    record: recordOf(teamOf.get(oppOwner.id)),
                    projected: oppLineup?.projected ?? 0,
                }
                : null,
            isPlayoff: opp?.isPlayoff ?? false,
        });
    }
    return {
        season: lineups.season,
        week,
        teams: out,
        byeTeams: byeTeams(schedule, week),
        // Best projection first: the wire is read top-down, and a 10%-owned
        // third-stringer should not sit above a 4%-owned starter because more
        // leagues happen to roster him.
        freeAgents: freeAgents
            .map((f) => {
            const entry = depth?.byPlayer[f.playerId];
            return {
                ...f,
                game: nflGameFor(schedule, f.proTeamId || f.proTeam, week),
                ...(entry ? { depth: depthLabel(entry) } : {}),
            };
        })
            .sort((a, b) => b.projected - a.projected || b.percentOwned - a.percentOwned),
    };
}
/** The standings, money and this week's projections, one row per owner. */
export function buildPowerRankingInput(data, money, lineups, sheetTeams) {
    const teamOf = new Map(data.teams.map((t) => [t.ownerId, t]));
    const ap = new Map(allPlay(data).map((r) => [r.ownerId, r]));
    const net = new Map(money.owners.map((o) => [o.ownerId, o.net]));
    const proj = new Map(lineups.teams.map((t) => [t.ownerId, t.projected]));
    const starters = new Map(lineups.teams.map((t) => [
        t.ownerId,
        t.starters.map((p) => `${p.slot} ${p.name}`).join(', ') || '(no starters set)',
    ]));
    const summary = new Map(sheetTeams.map((t) => [t.ownerId, t.summary]));
    return data.owners.map((o) => {
        const team = teamOf.get(o.id);
        const row = ap.get(o.id);
        return {
            ownerId: o.id,
            ownerName: o.name,
            teamName: o.teamName,
            record: recordOf(team),
            pointsFor: team?.pointsFor ?? 0,
            pointsAgainst: team?.pointsAgainst ?? 0,
            allPlayRecord: row ? `${row.allPlayWins}-${row.allPlayLosses}` : '0-0',
            moneyNet: net.get(o.id) ?? 0,
            projected: proj.get(o.id) ?? 0,
            summary: summary.get(o.id) ?? '',
            starters: starters.get(o.id) ?? '(no lineup stored)',
        };
    });
}
// ---------------------------------------------------------------------------
// Decisions the app makes without a model
// ---------------------------------------------------------------------------
const normalizeName = (s) => s.trim().toLowerCase();
/**
 * The viewer's team first, everything else in the order given. The join is by
 * normalized display name — the same rule avatars use — and a viewer who
 * matches nobody gets the list untouched rather than a guess.
 */
export function myTeamFirst(teams, owners, viewerName) {
    if (!viewerName)
        return teams;
    const wanted = normalizeName(viewerName);
    const mine = owners.find((o) => normalizeName(o.name) === wanted);
    if (!mine)
        return teams;
    const idx = teams.findIndex((t) => t.ownerId === mine.id);
    if (idx <= 0)
        return teams;
    return [...teams.slice(idx, idx + 1), ...teams.slice(0, idx), ...teams.slice(idx + 1)];
}
const ALERT_REASON = {
    OUT: 'out',
    INJURY_RESERVE: 'ir',
    SUSPENSION: 'suspended',
};
/**
 * Starters who cannot score this week: on bye, ruled out, on IR, or suspended.
 * QUESTIONABLE and DOUBTFUL are a judgment call and stay out of it. A starter
 * whose game has already kicked off is skipped too — the slot is locked, and a
 * nudge about it is just a taunt.
 */
export function lineupAlerts(lineups, schedule, week, now) {
    const out = [];
    for (const team of lineups.teams) {
        for (const p of team.starters) {
            const game = nflGameFor(schedule, p.proTeamId ?? p.proTeam, week);
            const kickoff = game && !game.bye ? game.kickoff : null;
            if (kickoff && Date.parse(kickoff) <= now.getTime())
                continue;
            const reason = game?.bye
                ? 'bye'
                : p.injury
                    ? ALERT_REASON[p.injury]
                    : undefined;
            if (!reason)
                continue;
            out.push({
                ownerId: team.ownerId,
                playerId: p.playerId,
                name: p.name,
                slot: p.slot,
                position: p.position,
                reason,
                kickoff,
            });
        }
    }
    return out;
}
//# sourceMappingURL=tip-sheet.js.map